import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./listingDetails.css";
import GHLFormModal from "../../Components/Modals/GHLFormModal";
import { useNewUser } from "../../APIContext/NewUserContext";
import Loading from "../../Components/LoaderNew";
import noImage from "../../Assets/Images/no-pictures.png";

const ListingDetails = () => {
  // Added fetchLocations here
  const { fetchPracticeListingByIDs, fetchLocations } = useNewUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [openForm, setOpenForm] = useState(false);
  const [data, setData] = useState([]);
  const [locations, setLocations] = useState([]); // New state for locations
  const [refCode, setRefCode] = useState("");
  const [loading, setLoading] = useState(true);

  const ids = searchParams.get("ids");

  const getFirstImage = (value) => {
    if (!value) return null;

    if (value === "[]") return null;
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : null;
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
        return null;
      } catch (err) {
        const urls = value.split(",").map((url) => url.trim());
        if (urls.length > 0 && urls[0] !== "") {
          return urls[0];
        }
      }
    }

    return null; // Final fallback
  };

  const formatLocation = (value = "") =>
    value
      ?.replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  /* ===============================
     Fetch Data (Updated to fetch both)
  ================================ */
  useEffect(() => {
    if (!ids) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch listings and locations in parallel
        const [response, locationsRes] = await Promise.all([
          fetchPracticeListingByIDs(ids),
          fetchLocations(),
        ]);

        setData(Array.isArray(response) ? response : []);

        if (locationsRes?.locations) {
          setLocations(locationsRes.locations);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ids, fetchPracticeListingByIDs, fetchLocations]);

  /* ===============================
     Extract Ref Codes
  ================================ */
  useEffect(() => {
    if (!data.length) return;

    const refCodes = data
      .map((item) => item.listing_ref_code)
      .filter(Boolean)
      .map((code) => code.replace(/#/g, ""))
      .join(",");

    setRefCode(refCodes);
  }, [data]);

  const handleRequestInfo = () => {
    setOpenForm(true);
  };

  return (
    <div className="listing-details-page">
      <div className="details-container">
        {/* Header */}
        <div
          style={{
            marginBottom: "20px",
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <button className="back-btn" onClick={() => navigate(-1)}>
            « Back to Listings
          </button>

          <button className="primary-btn" onClick={handleRequestInfo}>
            Request Additional Information
          </button>
        </div>

        {/* ===============================
            Loading State
        ================================ */}
        {loading && <Loading text="Loading listing details..." />}

        {/* ===============================
            Data State
        ================================ */}
        {!loading && data.length > 0 && (
          <>
            {data.map((listing) => {
              // Find the matching location object
              const matchedLocation = locations.find(
                (l) => l.slug === listing.listing_location,
              );

              // Extract listing specific image
              const listingImage = getFirstImage(listing.data_room);

              // Priority Logic: Listing Image -> Location Default -> noImage Fallback
              const displayImage = listingImage
                ? listingImage
                : matchedLocation?.default_image
                  ? matchedLocation.default_image
                  : noImage;

              return (
                <div key={listing.record_id} className="listing-block">
                  <h1 className="details-title">
                    Listing –{" "}
                    {listing.listing_title || ""}
                  </h1>

                  <div className="details-content">
                    <div className="details-text">
                      <p>
                        <strong>Status:</strong>{" "}
                        {formatLocation(listing.listing_status) || "N/A"}
                      </p>
                      <p>
                        <strong>Location:</strong>{" "}
                        {matchedLocation?.name || formatLocation(listing.listing_location) || "N/A"}
                      </p>
                      <p>
                        <strong>City:</strong>{" "}
                        {/* Use matched location name if available, fallback to formatting */}
                        {formatLocation(listing.city) || "-"}
                      </p>

                      <p>
                        <strong>Practice Type:</strong>{" "}
                        {formatLocation(listing.practice_listing_type)}
                      </p>
                      <p>
                        <strong>Description: </strong>
                        {formatLocation(listing?.description) || "N/A"}
                      </p>
                    </div>

                    <div className="details-image">
                      {/* Updated to use the new displayImage variable */}
                      <img src={displayImage} alt="Listing" />
                    </div>
                  </div>

                  <hr className="listing-divider" />
                </div>
              );
            })}
          </>
        )}

        {/* ===============================
            Empty State
        ================================ */}
        {!loading && data.length === 0 && (
          <div className="no-data">
            <p>No listings found.</p>
          </div>
        )}
      </div>

      {/* GHL Modal */}
      <GHLFormModal
        isOpen={openForm}
        onClose={() => setOpenForm(false)}
        refCodes={refCode}
      />
    </div>
  );
};

export default ListingDetails;
